package com.example.mystybag.login.views

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.RadioButton
import androidx.compose.material3.RadioButtonDefaults
import androidx.compose.material3.LocalMinimumInteractiveComponentSize
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.unit.dp
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.LocalSoftwareKeyboardController
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.sp
import com.arkivanov.decompose.extensions.compose.subscribeAsState
import androidx.compose.foundation.Image
import com.example.mystybag.common.extensions.AsyncImage
import com.example.mystybag.common.extensions.noRippleClickable
import com.example.mystybag.communication.defines.CommunicationDefines.Companion.DOMAIN
import com.example.mystybag.core.resources.SRC
import com.example.mystybag.core.expected.PlatformHelper
import com.example.mystybag.core.expected.rememberIsWebPortraitLayout
import com.example.mystybag.core.views.navBarBottomPadding
import dev.icerock.moko.resources.compose.painterResource
import com.example.mystybag.common.ui.components.custom.SearchTextField
import com.example.mystybag.common.ui.components.native.DefaultButton
import com.example.mystybag.common.ui.components.native.Texts
import com.example.mystybag.common.ui.views.BottomSheetUIView
import com.example.mystybag.core.ui.theme.LocalTheme.p12
import com.example.mystybag.core.ui.theme.LocalTheme.p16
import com.example.mystybag.core.ui.theme.LocalTheme.p24
import com.example.mystybag.core.ui.theme.LocalTheme.p0
import com.example.mystybag.core.ui.theme.LocalTheme.p48
import com.example.mystybag.core.ui.theme.LocalTheme.p64
import com.example.mystybag.core.ui.theme.LocalTheme.p8
import com.example.mystybag.core.views.AppTheme
import com.example.mystybag.login.helpers.navigateToLoginHelp
import com.example.mystybag.login.views.components.WebInlineLanguageSwitcher
import com.example.mystybag.login.logic.ChooseCityViewModel
import dev.icerock.moko.resources.compose.stringResource
import com.example.mystybag.common.resources.SR
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.core.ui.theme.LocalTheme.p20
import com.example.mystybag.core.ui.theme.LocalTheme.p32

private val WELCOME_LOGO: String
    get() = "https://$DOMAIN/static/email_photos/logo_40.png"

@Composable
fun ChooseCityView(viewModel: ChooseCityViewModel) {
    if (PlatformHelper.isWeb && !rememberIsWebPortraitLayout()) {
        ChooseCityViewWeb(viewModel)
        return
    }

    ChooseCityViewMobile(viewModel)
}

@Composable
private fun ChooseCityViewWeb(viewModel: ChooseCityViewModel) {
    val keyboardController = LocalSoftwareKeyboardController.current
    val uiState = viewModel.uiState.subscribeAsState()
    val theme = AppTheme.current
    Column(
        modifier = Modifier.fillMaxSize(),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .height(106.dp)
                .padding(horizontal = 40.dp, vertical = 32.dp),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            AsyncImage(
                link = WELCOME_LOGO,
                placeholder = null,
                modifier = Modifier
                    .width(180.dp)
                    .height(42.dp),
                contentScale = ContentScale.Fit
            )
            Row(
                horizontalArrangement = Arrangement.spacedBy(24.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Texts.BodyMedium(
                    text = "Help",
                    color = theme.colors.onSurfaceVariant,
                    modifier = Modifier.noRippleClickable { viewModel.navigateToLoginHelp() }
                )
                WebInlineLanguageSwitcher()
            }
        }

        Box(
            modifier = Modifier.fillMaxSize(),
            contentAlignment = Alignment.TopCenter
        ) {
            Column(
                modifier = Modifier
                    .width(530.dp)
                    .padding(top = 32.dp, bottom = 32.dp),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                Texts.DisplaySmall(
                    text = stringResource(SR.strings.city),
                    modifier = Modifier.fillMaxWidth(),
                    textAlign = TextAlign.Center,
                    color = theme.colors.onSurface
                )
                Spacer(modifier = Modifier.height(24.dp))
                Texts.BodyLarge(
                    text = sharedString(SR.strings.city_select_description),
                    textAlign = TextAlign.Center,
                    modifier = Modifier.fillMaxWidth(),
                    color = theme.colors.onSurfaceVariant
                )
                Spacer(modifier = Modifier.height(24.dp))

                val filteredCities = if (uiState.value.cities.size < 10 || uiState.value.searchText.isEmpty()) {
                    uiState.value.cities
                } else {
                    uiState.value.cities.filter {
                        it.cityName.contains(uiState.value.searchText, ignoreCase = true)
                    }
                }

                if (uiState.value.cities.size >= 10) {
                    SearchTextField(
                        modifier = Modifier.fillMaxWidth(),
                        searchCallbacks = viewModel.searchControls,
                        searchHint = sharedString(SR.strings.search_city),
                        maxLines = 1
                    )
                    Spacer(modifier = Modifier.height(12.dp))
                }

                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .verticalScroll(rememberScrollState())
                        .weight(1f, fill = false)
                ) {
                    filteredCities.forEachIndexed { index, city ->
                        if (index > 0) {
                            HorizontalDivider(
                                modifier = Modifier.fillMaxWidth(),
                                thickness = 1.dp,
                                color = AppTheme.current.colors.outlineVariant.copy(alpha = 0.5f)
                            )
                        }
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .clickable {
                                    keyboardController?.hide()
                                    viewModel.onCitySelected(city)
                                }
                                .padding(vertical = p20),
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.spacedBy(p16)
                        ) {
                            CompositionLocalProvider(LocalMinimumInteractiveComponentSize provides p0) {
                                RadioButton(
                                    modifier = Modifier.size(p24),
                                    selected = uiState.value.selectedCity?.id == city.id,
                                    onClick = {
                                        keyboardController?.hide()
                                        viewModel.onCitySelected(city)
                                    },
                                    colors = RadioButtonDefaults.colors(
                                        selectedColor = AppTheme.current.colors.primary,
                                        unselectedColor = AppTheme.current.colors.outline
                                    )
                                )
                            }
                            Texts.TitleMedium(
                                text = city.cityName,
                                color = AppTheme.current.colors.onSurface,
                                modifier = Modifier.weight(1f)
                            )
                        }
                    }
                }

                if ((uiState.value.cities.size < 10 || uiState.value.searchText.isNotEmpty()) && uiState.value.cities.isNotEmpty()) {
                    Spacer(modifier = Modifier.height(48.dp))
                }

                DefaultButton(
                    modifier = Modifier.fillMaxWidth(),
                    caption = stringResource(SR.strings.next),
                    callback = {
                        keyboardController?.hide()
                        viewModel.onNextClicked()
                    },
                    identifier = "ChooseCity Screen Next Web"
                )
            }
        }
    }
}

@Composable
private fun ChooseCityViewMobile(viewModel: ChooseCityViewModel) {
    val keyboardController = LocalSoftwareKeyboardController.current

    Box(modifier = Modifier.fillMaxSize()) {
        BottomSheetUIView(
            viewModel = viewModel,
            showTopBar = true,
            navigationContent = {
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = p16)
                ) {
                    Box(
                        modifier = Modifier
                            .size(p48)
                            .clip(CircleShape)
                            .background(AppTheme.current.colors.surfaceContainerLowest)
                            .noRippleClickable {
                                keyboardController?.hide()
                                viewModel.navCallbacks.backButtonCallback()
                            },
                        contentAlignment = Alignment.Center
                    ) {
                        Image(
                            painter = painterResource(SRC.images.arrow_back),
                            contentDescription = null,
                            contentScale = ContentScale.Fit,
                            modifier = Modifier.size(width = 10.dp, height = 18.dp),
                            colorFilter = ColorFilter.tint(AppTheme.current.colors.primary)
                        )
                    }
                    Spacer(modifier = Modifier.height(p32))
                    Texts.DisplaySmall(
                        text = stringResource(SR.strings.city),
                        modifier = Modifier.fillMaxWidth(),
                        textAlign = TextAlign.Start,
                        color = AppTheme.current.colors.onSurface
                    )
                }
            },
            bottomSheetFactory = {}
        ) { uiState ->
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .fillMaxSize()
                    .padding(start = p16, end = p16, top = p16),
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {

                    if (uiState.value.isLoading) {
                        // Show loading indicator
                    } else {
                        Column(
                            modifier = Modifier
                                .fillMaxWidth()
                                .verticalScroll(rememberScrollState())
                                .padding( bottom = p16)
                        ) {
                            // Description
                            Texts.BodyLarge(
                                text = sharedString(SR.strings.city_select_description),
                                textAlign = TextAlign.Start,
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .padding(bottom = p20)
                            )
                            
                            // Search field – show only when there are 10 or more cities
                            if (uiState.value.cities.size >= 10) {
                                SearchTextField(
                                    modifier = Modifier.fillMaxWidth(),
                                    searchCallbacks = viewModel.searchControls,
                                    searchHint = sharedString(SR.strings.search_city),
                                    maxLines = 1
                                )
                            }
                            
                            // Cities list
                            val filteredCities = if (uiState.value.cities.size < 10 || uiState.value.searchText.isEmpty()) {
                                uiState.value.cities
                            } else {
                                uiState.value.cities.filter {
                                    it.cityName.contains(uiState.value.searchText, ignoreCase = true)
                                }
                            }
                            
                            filteredCities.forEachIndexed { index, city ->
                                if (index > 0) {
                                    HorizontalDivider(
                                        modifier = Modifier.fillMaxWidth(),
                                        thickness = 1.dp,
                                        color = AppTheme.current.colors.outlineVariant.copy(alpha = 0.5f)
                                    )
                                }
                                Row(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .clickable {
                                            keyboardController?.hide()
                                            viewModel.onCitySelected(city)
                                        }
                                        .padding(vertical = p20),
                                    verticalAlignment = Alignment.CenterVertically,
                                    horizontalArrangement = Arrangement.spacedBy(p16)
                                ) {
                                    CompositionLocalProvider(LocalMinimumInteractiveComponentSize provides p0) {
                                        RadioButton(
                                            modifier = Modifier.size(p24),
                                            selected = uiState.value.selectedCity?.id == city.id,
                                            onClick = {
                                                keyboardController?.hide()
                                                viewModel.onCitySelected(city)
                                            },
                                            colors = RadioButtonDefaults.colors(
                                                selectedColor = AppTheme.current.colors.primary,
                                                unselectedColor = AppTheme.current.colors.outline
                                            )
                                        )
                                    }
                                    Texts.TitleMedium(
                                        text = city.cityName,
                                        color = AppTheme.current.colors.onSurface,
                                        modifier = Modifier.weight(1f)
                                    )
                                }
                            }
                            
                            if (filteredCities.isEmpty() && uiState.value.searchText.isNotEmpty()) {
                                Texts.BodyMedium(
                                    text = "No cities found",
                                    textAlign = TextAlign.Center,
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .padding(top = p16)
                                )
                            }
                        }
                    }

                Spacer(Modifier.weight(1f))

                // Next button
                DefaultButton(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(bottom = p8),
                    caption = stringResource(SR.strings.next),
                    callback = {
                        keyboardController?.hide()
                        viewModel.onNextClicked()
                    },
                    identifier = "ChooseCity Screen Next"
                )
            }
        }
    }
}

